<template>
  <v-app-bar
    class="bg-color_b kp-app-bar position-fixed"
    :extension-height="smAndUp?32: undefined"
    location="top"
    flat
  >
    <template #prepend>
      <v-app-bar-nav-icon
        rounded
        class="kp-app-bar__burger-button"
        :class="burgerButtonClasses"
        data-kp-anchor="layout.default.menu-button"
        @click="handleIconClick"
      >
        <v-icon
          :class="menuIconClass"
          :size="32"
          icon="menu_open"
        />
      </v-app-bar-nav-icon>
    </template>
    <template #title>
      <RouterLink :to="currentUserHomePage">
        <img
          :src="logo"
          alt="Keyprod :: Go to Home"
          class="kp-app-bar__logo-img"
        >
      </RouterLink>
    </template>

    <!-- DateTime -->
    <KpDateTime v-if="smAndUp" />
    <v-divider
      v-if="smAndUp"
      vertical
      thickness="1"
      length="30"
      color="color_g"
      class="mx-2 mt-4 border-opacity-100"
    />

    <!-- Help-->
    <v-btn
      v-click-outside="{handler: closeResourceCenter, include}"
      rounded
      icon="help"
      class="kp-app-bar__help"
      data-kp-anchor="layout.default.help-button"
      @click="toggleResourceCenter"
    >
      <v-badge
        color="primary"
        :content="userflowAnnouncementCount"
        :model-value="showUserflowBadge"
      >
        <v-icon
          icon="help no-fill"
        />
      </v-badge>
      <v-tooltip
        open-on-hover
        activator="parent"
        location="bottom"
      >
        {{ t('navigation.applicationBar.help') }}
      </v-tooltip>
    </v-btn>

    <!-- Notifications-->
    <v-btn
      rounded
      icon="notifications_none"
      :title="notificationTotal"
      data-kp-anchor="layout.default.notifications-button"
      data-cy="notifications-btn"
      @click="toggleNavigationDrawer()"
    >
      <v-badge
        :data-cy="`notifications__badge-${notificationTotal}`"
        color="primary"
        :model-value="notificationTotal > 0"
        dot
        overlap
      >
        <v-icon
          icon="notifications_none no-fill"
        />
      </v-badge>
      <v-tooltip
        open-on-hover
        activator="parent"
        location="bottom"
      >
        {{ t('menu.secondary.notifications') }}
      </v-tooltip>
    </v-btn>
    <!-- Profile -->
    <KpAppBarProfileMenu />
    <template
      v-if="mdAndUp"
      #extension
    >
      <div
        class="kp-app-bar__breadcrumb-container d-flex justify-space-between align-center
          bg-color_c h-100 w-100 border-b-thin"
        :class="`px-${layoutOutsideSpacing}`"
      >
        <KpBreadcrumbs id="kp-app-bar-breadcrumbs" />
      </div>
    </template>
  </v-app-bar>
  <v-navigation-drawer
    v-model="showNotificationsDrawer"
    temporary
    location="right"
    width="400"
    disable-resize-watcher
    disable-route-watchers
  >
    <NotificationsCenter
      v-if="showNotificationsDrawer"
      v-model="showNotificationsDrawer"
      :class="{'is-responsive': $vuetify.display.mobile}"
      class="navbar-top__notifications"
      @cancel="showNotificationsDrawer = false"
    />
  </v-navigation-drawer>
  <!-- First-login modal -->
  <ConfirmDialog
    v-if="showFirstLoginDialog"
    extra-content-class="kp-app-bar__first-login"
    :show-dialog="showFirstLoginDialog"
    :title="$t('firstLogin.welcome')"
    :comfirm-button-label="$t('firstLogin.start')"
    :show-cancel-button="false"
    persistent
    @dialog="handleFirstLoginDialogConfirm"
  >
    <template #default>
      <div
        class="d-flex flex-column justify-center align-center ga-6 pb-4"
        data-kp-anchor="layout.default.welcome-message"
      >
        <h1 class="text-headline-large mb-4 mt-0">
          {{ $t('firstLogin.welcomeToKeyprod') }}
        </h1>
        <!-- eslint-disable vue/no-v-html -->
        <h2
          class="text-body-large text-center my-0"
          v-html="modalTextForRole"
        />
        <!-- eslint-disable vue/no-v-html -->
        <h2
          class="text-body-large my-0"
          v-html="$t('firstLogin.enjoyExploring')"
        />
      </div>
      <div class="d-flex justify-center align-center">
        <img
          :src="spaceshipLaunch"
          alt="Spaceship Launch"
        >
      </div>
    </template>
    <template #footer>
      <div class="flex-fill d-flex flex-row justify-end pt-5 pb-6 px-6 border-t">
        <v-btn
          class="bg-black"
          color="primary"
          data-kp-anchor="layout.default.welcome-start-button"
          data-cy="confirm-dialog__button-yes"
          :loading="updatingFirstLogin"
          :text="t('firstLogin.start')"
          @click="handleFirstLoginDialogConfirm"
        />
      </div>
    </template>
  </ConfirmDialog>
</template>
<script setup>
import {
  computed, defineAsyncComponent, defineModel, inject, ref, onMounted,
} from 'vue';
import { useRouter } from 'vue-router';
import { useDisplay } from 'vuetify';
import { useQuery, useMutation } from '@vue/apollo-composable';
import gql from 'graphql-tag';
import userflow from 'userflow.js';
import dayjs from 'dayjs';
import { useI18n } from 'vue-i18n';
import { useIntervalFn } from '@vueuse/core';
import { useStore } from '~/store';
import { UPDATE_ME } from '~/graphql/mutations/user';
import useAppLayout from '~/composables/useAppLayout';
import useAuth from '~/composables/useAuth';
import useNotification from '~/composables/useNotification';
import useUserflow from '~/composables/useUserflow';
import useRoutes from '~/composables/useRoutes';
import useTracking from '~/composables/tracking/useTracking';

import spaceshipLaunch from '~images/keyprod_spaceship.svg';

import { CORE_DASHBOARD_FIELDS, ME_USER_FIELDS } from '~/graphql/fragments';
import * as types from '~/store/mutationTypes';

import KpBreadcrumbs from '~/layouts/top/KpBreadcrumbs.vue';
import KpDateTime from '~/layouts/top/KpDateTime.vue';
import KpAppBarProfileMenu from '~/layouts/KpAppBarProfileMenu.vue';

import ConfirmDialog from '~/components/common/others/ConfirmDialog.vue';

const NotificationsCenter = defineAsyncComponent(() => import('~/pages/navigation/top/components/notifications/NotificationsCenter.vue'));
const router = useRouter();
const { t } = useI18n();
const { trackEvent } = useTracking();
const store = useStore();

// Apollo mutation to set firstLogin date
const {
  mutate: updateFirstLogin,
  onDone: onDoneUpdateFirstLogin,
  loading: updatingFirstLogin,
} = useMutation(UPDATE_ME);

onDoneUpdateFirstLogin((result) => {
  // Update user in store with new firstLogin value
  store.commit(`auth/${types.FETCH_USER_SUCCESS}`, { user: result?.data?.updateMe });
});

const {
  userFirstLogin,
  activeUser,
} = useAuth();
/**
 * Manage the visibility of the notification drawer
 */
const showNotificationsDrawer = inject('showNotificationsDrawer');

const {
  smAndUp,
  mdAndUp,
} = useDisplay();
const { notificationTotal } = useNotification();

const { currentUserHomePage } = useRoutes();

/**
 * Tells if the drawer is open
 */
const model = defineModel({ type: Boolean, default: false });

/**
 * Handle the click event for the icon
 */
function handleIconClick() {
  model.value = !model.value;
}

function toggleNavigationDrawer() {
  showNotificationsDrawer.value = !showNotificationsDrawer.value;
  const action = showNotificationsDrawer.value ? 'Opened' : 'Closed';
  // Set the notification total to null when the drawer is opened
  // So we can call NotificationTotal query to refresh the notification count
  // Because the query is disabled when the notification total > 0
  if (showNotificationsDrawer.value) {
    notificationTotal.value = null;
  }
  trackEvent('Notification Panel Used', { action });
}

/**
 * Return the appropriate logo depending on the size of the screen
 */
const logo = computed(() => (smAndUp.value
  ? new URL('~images/KeyProd-Logo-White--Transparent-VECTO.svg', import.meta.url).href
  : new URL('~images/KeyProd-Icon-Red--Transparent-VECTO.svg', import.meta.url).href));

const menuIconClass = computed(() => (model.value ? 'arrow-down' : 'arrow-down-open'));

// Query.me in order to refresh the assigned dashboards/name/avatar
const { onResult: onResultMe } = useQuery(
  gql`
    query Me {
      me {
        ...MeUserFields
        dashboardsExpand {
        ...CoreDashboardFields
        },
        groupsExpand {
          id
        }
      }
    }
    ${ME_USER_FIELDS}
    ${CORE_DASHBOARD_FIELDS}
  `,
  {},
  {
    pollInterval: 300000,
  },
);
onResultMe((result) => {
  store.commit(`auth/${types.FETCH_USER_SUCCESS}`, { user: result.data?.me });
  store.commit('dashboard/commitUserDashboards', result.data?.me?.dashboardsExpand ?? []);
});

// Userflow stuff
const { isUserflowEnabled } = useUserflow();
// Number of unread announcements
const userflowAnnouncementCount = ref(0);
// Show the badge or not on the "help" button
const showUserflowBadge = computed(
  () => isUserflowEnabled() && userflowAnnouncementCount.value > 0,
);
/**
 * Refresh the number of unread announcements
 */
function refreshUserflowAnnouncementCount() {
  if (!isUserflowEnabled()) {
    return;
  }

  const resourceCenterState = userflow.getResourceCenterState();
  if (resourceCenterState?.unreadAnnouncementCount !== undefined
   && resourceCenterState?.unreadAnnouncementCount !== null) {
    userflowAnnouncementCount.value = resourceCenterState.unreadAnnouncementCount;
  }
}
/**
 * Toggles the Userflow resource center
 */
function toggleResourceCenter() {
  showNotificationsDrawer.value = false;
  if (isUserflowEnabled()) {
    userflow.toggleResourceCenter();
    refreshUserflowAnnouncementCount();
  }
}

/**
 * Closes the Userflow resource center
 */
function closeResourceCenter() {
  if (isUserflowEnabled()) {
    userflow.closeResourceCenter();
    refreshUserflowAnnouncementCount();
  }
}
// Periodically refresh the number of unread annoucements
useIntervalFn(
  () => {
    refreshUserflowAnnouncementCount();
  },
  10000,
);

/**
 * Lists the elements that will not close the resource center
 */
function include() {
  // This is the native class of Userflow resource center
  return [document.querySelector('.userflowjs-resource-center')];
}

// Styles stuff
const { layoutOutsideSpacing } = useAppLayout();
const burgerButtonClasses = computed(() => {
  const classes = [`ml-${layoutOutsideSpacing.value}`];
  if (model.value) classes.push('is-opened');

  return classes;
});

// --------------------------------------------------------------
// First-login modal logic
// --------------------------------------------------------------

// Controls the visibility of the first-login modal
const showFirstLoginDialog = ref(false);

// Handles confirm action from the first-login modal
async function handleFirstLoginDialogConfirm() {
  // Update firstLogin date
  if (userFirstLogin.value) {
    const firstLoginDate = dayjs()
      .utc()
      .format('YYYY-MM-DD HH:mm:ss');
    await updateFirstLogin({ firstLogin: firstLoginDate });
  }

  showFirstLoginDialog.value = false;
  // Redirect to dashboard "Machine Status"
  await router.push({
    name: 'dashboardId',
    params: { value: 1005 },
  });
}

onMounted(async () => {
  // If it is the very first login for this user, open the modal
  if (userFirstLogin.value) {
    await trackEvent('Sign Up Started', {});
    showFirstLoginDialog.value = true;
  }

  // Try to know the number of unread annoucements in Userflow
  refreshUserflowAnnouncementCount();
});

const modalTextForRole = computed(() => {
  if (activeUser.value?.access_code === 'simplified_operator') {
    return t('firstLogin.operatorText');
  }
  return t(`firstLogin.${activeUser.value?.access_code}Text`);
});

</script>

<style lang="scss">
.kp-app-bar {
  .v-toolbar-title__placeholder {
    overflow: visible; // the tail of the logo needs to be visible
  }

  .v-toolbar__content {
    .v-toolbar-title {
      margin-inline-start: v-bind('smAndUp ? "2px" : "0px"') !important;

      img {
        width: v-bind('smAndUp ? "130px" : "28px"') !important;
      }
    }

    .v-toolbar__prepend {
      margin-inline: 0 !important;
    }
  }

  &__logo-img {
    height: 2rem;
    transform: translate3d(0, 30%, 0);
  }
}
</style>
